import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface SearchConfig {
  id: string;
  entityType: 'ideas' | 'campaigns' | 'users' | 'venues' | 'sessions';
  searchableFields: string[];
  weights: Record<string, number>;
  synonyms: Record<string, string[]>;
  stopWords: string[];
  enabled: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface SearchFilter {
  id: string;
  name: string;
  type: 'range' | 'multiselect' | 'boolean' | 'date';
  entityType: string;
  options?: string[];
  defaultValue?: any;
  order: number;
  enabled: boolean;
  createdAt: Date;
}

interface SearchAnalytic {
  id: string;
  query: string;
  entityType: string;
  resultsCount: number;
  userId?: string;
  userName?: string;
  timestamp: Date;
  clickedResults: string[];
}

interface SearchSuggestion {
  id: string;
  query: string;
  suggestion: string;
  frequency: number;
  entityType: string;
  createdAt: Date;
}

const SearchManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'search' | 'analytics'>('search');
  const [searchConfigs, setSearchConfigs] = useState<SearchConfig[]>([]);
  const [filters, setFilters] = useState<SearchFilter[]>([]);
  const [analytics, setAnalytics] = useState<SearchAnalytic[]>([]);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showApplyFilterModal, setShowApplyFilterModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [searching, setSearching] = useState(false);
  const [editingConfig, setEditingConfig] = useState<SearchConfig | null>(null);
  const [editingFilter, setEditingFilter] = useState<SearchFilter | null>(null);

  // Config form state
  const [configForm, setConfigForm] = useState({
    entityType: 'ideas' as SearchConfig['entityType'],
    searchableFields: '',
    weights: '',
    synonyms: '',
    stopWords: '',
    enabled: true
  });

  // Filter form state
  const [filterForm, setFilterForm] = useState({
    name: '',
    type: 'multiselect' as SearchFilter['type'],
    entityType: 'ideas',
    options: '',
    defaultValue: '',
    order: 0,
    enabled: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadFilters(),
        loadAnalytics()
      ]);
    } catch (error) {
      console.error('Error loading search data:', error);
    } finally {
      setLoading(false);
    }
  };


  const loadFilters = async () => {
    const q = query(collection(db, 'search_filters'), orderBy('order', 'asc'));
    const snapshot = await getDocs(q);
    const filtersData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate()
    })) as SearchFilter[];
    setFilters(filtersData);
  };

  const loadAnalytics = async () => {
    const q = query(collection(db, 'search_analytics'), orderBy('timestamp', 'desc'));
    const snapshot = await getDocs(q);
    const analyticsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate()
    })) as SearchAnalytic[];
    setAnalytics(analyticsData.slice(0, 100)); // Last 100 searches
  };



  const handleSaveFilter = async () => {
    try {
      const filterData = {
        name: filterForm.name,
        type: filterForm.type,
        entityType: filterForm.entityType,
        options: filterForm.options ? filterForm.options.split(',').map(o => o.trim()).filter(o => o) : [],
        defaultValue: filterForm.defaultValue || null,
        order: filterForm.order,
        enabled: filterForm.enabled,
        updatedAt: Timestamp.now()
      };

      if (editingFilter) {
        await updateDoc(doc(db, 'search_filters', editingFilter.id), filterData);
      } else {
        await addDoc(collection(db, 'search_filters'), {
          ...filterData,
          createdAt: Timestamp.now()
        });
      }

      setShowFilterModal(false);
      setEditingFilter(null);
      resetFilterForm();
      loadFilters();
    } catch (error) {
      console.error('Error saving filter:', error);
    }
  };


  const handleDeleteFilter = async (filterId: string) => {
    if (window.confirm(t('search.confirmDeleteFilter'))) {
      try {
        await deleteDoc(doc(db, 'search_filters', filterId));
        loadFilters();
      } catch (error) {
        console.error('Error deleting filter:', error);
      }
    }
  };


  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      // Keresés több kollekcióban
      const collections = ['ideas', 'campaigns', 'users', 'venues', 'sessions'];
      const results: any[] = [];

      for (const collectionName of collections) {
        const snapshot = await getDocs(collection(db, collectionName));
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const searchableText = JSON.stringify(data).toLowerCase();
          
          if (searchableText.includes(searchQuery.toLowerCase())) {
            // Szűrők alkalmazása
            let matchesFilters = true;
            Object.entries(activeFilters).forEach(([filterName, filterValue]) => {
              const filter = filters.find(f => f.name === filterName);
              if (filter && filterValue) {
                if (filter.type === 'boolean' && data[filterName] !== filterValue) {
                  matchesFilters = false;
                }
                if (filter.type === 'multiselect' && !filterValue.includes(data[filterName])) {
                  matchesFilters = false;
                }
              }
            });

            if (matchesFilters) {
              results.push({
                id: doc.id,
                type: collectionName,
                ...data
              });
            }
          }
        });
      }

      setSearchResults(results);

      // Analitika mentése
      await addDoc(collection(db, 'search_analytics'), {
        query: searchQuery,
        entityType: 'all',
        resultsCount: results.length,
        userId: user?.uid,
        userName: user?.displayName,
        timestamp: Timestamp.now(),
        clickedResults: []
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const applyFilter = (filterName: string, value: any) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
  };

  const resetFilterForm = () => {
    setFilterForm({
      name: '',
      type: 'multiselect',
      entityType: 'ideas',
      options: '',
      defaultValue: '',
      order: 0,
      enabled: true
    });
  };

  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'ideas': return '💡';
      case 'campaigns': return '💝';
      case 'users': return '👤';
      case 'venues': return '📍';
      case 'sessions': return '⚡';
      default: return '📋';
    }
  };

  const getFilterTypeIcon = (type: SearchFilter['type']) => {
    switch (type) {
      case 'range': return '📊';
      case 'multiselect': return '☑️';
      case 'boolean': return '🔘';
      case 'date': return '📅';
      default: return '🔍';
    }
  };

  // Analytics calculations
  const topSearches = [...analytics]
    .reduce((acc, item) => {
      const existing = acc.find(a => a.query === item.query);
      if (existing) {
        existing.count++;
      } else {
        acc.push({ query: item.query, count: 1, avgResults: item.resultsCount });
      }
      return acc;
    }, [] as { query: string; count: number; avgResults: number }[])
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const zeroResultSearches = analytics.filter(a => a.resultsCount === 0).slice(0, 20);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-[#B8B8C3]">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-16 lg:pt-0 pb-16 sm:pb-0">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-4xl font-bold text-gradient-cyan mb-2">
            🔍 {t('search.title')}
          </h2>
          <p className="text-[#B8B8C3]">{t('search.description')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#31383D]">
        <button
          onClick={() => setActiveTab('search')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'search'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          🔍 {t('search.search')}
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'analytics'
              ? 'text-cyan-400 border-b-2 border-cyan-400'
              : 'text-[#B8B8C3] hover:text-white'
          }`}
        >
          📊 {t('search.analytics')}
        </button>
      </div>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div className="space-y-6">
          {/* Filter Management - Admin Only */}
          {user && (
            <div className="card-dark p-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">{t('search.manageFilters')}</h3>
                <button
                  onClick={() => {
                    resetFilterForm();
                    setEditingFilter(null);
                    setShowFilterModal(true);
                  }}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('search.addFilter')}
                </button>
              </div>
              
              {filters.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {filters.map(filter => (
                    <div key={filter.id} className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm flex items-center gap-2">
                      <span>{filter.name} ({t(`search.filterType.${filter.type}`)})</span>
                      <button
                        onClick={() => {
                          setEditingFilter(filter);
                          setFilterForm({
                            name: filter.name,
                            type: filter.type,
                            entityType: filter.entityType,
                            options: filter.options?.join(', ') || '',
                            defaultValue: filter.defaultValue || '',
                            order: filter.order,
                            enabled: filter.enabled
                          });
                          setShowFilterModal(true);
                        }}
                        className="hover:text-white"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDeleteFilter(filter.id)}
                        className="hover:text-white"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search Bar */}
          <div className="card-dark p-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={t('search.searchPlaceholder')}
                  className="input-dark w-full text-lg"
                />
              </div>
              <button
                onClick={() => setShowApplyFilterModal(true)}
                className="btn-secondary flex items-center gap-2 px-6"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {t('search.filters')}
                {Object.keys(activeFilters).length > 0 && (
                  <span className="px-2 py-1 bg-cyan-500 text-white rounded-full text-xs">
                    {Object.keys(activeFilters).length}
                  </span>
                )}
              </button>
              <button
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                className="btn-primary flex items-center gap-2 px-8"
              >
                {searching ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('search.searching')}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    {t('search.search')}
                  </>
                )}
              </button>
            </div>

            {/* Active Filters Display */}
            {Object.keys(activeFilters).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {Object.entries(activeFilters).map(([key, value]) => (
                  <div key={key} className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm flex items-center gap-2">
                    <span>{key}: {Array.isArray(value) ? value.join(', ') : String(value)}</span>
                    <button
                      onClick={() => {
                        const newFilters = { ...activeFilters };
                        delete newFilters[key];
                        setActiveFilters(newFilters);
                      }}
                      className="hover:text-white"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  onClick={clearFilters}
                  className="px-3 py-1 text-red-400 hover:text-red-300 text-sm"
                >
                  {t('search.clearAll')}
                </button>
              </div>
            )}
          </div>

          {/* Search Results */}
          <div className="space-y-3">
            {searchResults.length > 0 && (
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">
                  {searchResults.length} {t('search.resultsFound')}
                </h3>
              </div>
            )}

            {searchResults.map((result, index) => (
              <div key={`${result.type}-${result.id}`} className="card-dark p-6 hover:border-cyan-500/50 transition-colors">
                <div className="flex items-start gap-4">
                  <span className="text-3xl">{getEntityIcon(result.type)}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-white">
                        {result.title || result.name || result.displayName || result.id}
                      </h4>
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                        {result.type}
                      </span>
                    </div>
                    <p className="text-[#B8B8C3] text-sm line-clamp-2">
                      {result.description || result.email || t('search.noDescription')}
                    </p>
                    {result.createdAt && (
                      <p className="text-xs text-[#B8B8C3] mt-2">
                        {new Date(result.createdAt.seconds * 1000).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {searchQuery && searchResults.length === 0 && !searching && (
              <div className="card-dark p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('search.noResults')}</h3>
                <p className="text-[#B8B8C3]">{t('search.tryDifferentQuery')}</p>
              </div>
            )}

            {!searchQuery && searchResults.length === 0 && (
              <div className="card-dark p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-bold text-white mb-2">{t('search.startSearching')}</h3>
                <p className="text-[#B8B8C3]">{t('search.enterQuery')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-white mb-4">{t('search.searchAnalytics')}</h3>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('search.totalSearches')}</span>
                <span className="text-3xl">🔍</span>
              </div>
              <p className="text-3xl font-bold text-white">{analytics.length}</p>
            </div>

            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('search.uniqueQueries')}</span>
                <span className="text-3xl">💬</span>
              </div>
              <p className="text-3xl font-bold text-cyan-400">
                {new Set(analytics.map(a => a.query)).size}
              </p>
            </div>

            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('search.avgResults')}</span>
                <span className="text-3xl">📊</span>
              </div>
              <p className="text-3xl font-bold text-green-400">
                {analytics.length > 0 ? Math.round(analytics.reduce((sum, a) => sum + a.resultsCount, 0) / analytics.length) : 0}
              </p>
            </div>

            <div className="card-dark p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#B8B8C3] text-sm">{t('search.zeroResults')}</span>
                <span className="text-3xl">❌</span>
              </div>
              <p className="text-3xl font-bold text-red-400">
                {zeroResultSearches.length}
              </p>
            </div>
          </div>

          {/* Top Searches */}
          <div className="card-dark p-6">
            <h4 className="text-xl font-bold text-white mb-4">🔥 {t('search.topSearches')}</h4>
            <div className="space-y-2">
              {topSearches.map((search, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-[#15191E] rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-cyan-400">#{index + 1}</span>
                    <span className="text-white font-semibold">{search.query}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-[#B8B8C3]">{search.count} {t('search.searches')}</span>
                    <span className="text-green-400">{search.avgResults} {t('search.results')}</span>
                  </div>
                </div>
              ))}
            </div>

            {topSearches.length === 0 && (
              <p className="text-center text-[#B8B8C3] py-8">{t('search.noSearchData')}</p>
            )}
          </div>

          {/* Zero Result Searches */}
          {zeroResultSearches.length > 0 && (
            <div className="card-dark p-6">
              <h4 className="text-xl font-bold text-white mb-4">❌ {t('search.zeroResultQueries')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {zeroResultSearches.slice(0, 12).map((search, index) => (
                  <div key={index} className="p-3 bg-red-500/10 rounded-lg">
                    <p className="text-white text-sm truncate">{search.query}</p>
                    <p className="text-xs text-[#B8B8C3]">{search.timestamp.toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Edit Modal - Admin */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingFilter ? t('search.editFilter') : t('search.newFilter')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('search.filterName')}
                </label>
                <input
                  type="text"
                  value={filterForm.name}
                  onChange={(e) => setFilterForm({ ...filterForm, name: e.target.value })}
                  className="input-dark w-full"
                  placeholder={t('search.filterNamePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('search.filterType')}
                  </label>
                  <select
                    value={filterForm.type}
                    onChange={(e) => setFilterForm({ ...filterForm, type: e.target.value as SearchFilter['type'] })}
                    className="input-dark w-full"
                  >
                    <option value="multiselect">{t('search.filterType.multiselect')}</option>
                    <option value="range">{t('search.filterType.range')}</option>
                    <option value="boolean">{t('search.filterType.boolean')}</option>
                    <option value="date">{t('search.filterType.date')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('search.entityType')}
                  </label>
                  <select
                    value={filterForm.entityType}
                    onChange={(e) => setFilterForm({ ...filterForm, entityType: e.target.value })}
                    className="input-dark w-full"
                  >
                    <option value="ideas">{t('search.entity.ideas')}</option>
                    <option value="campaigns">{t('search.entity.campaigns')}</option>
                    <option value="users">{t('search.entity.users')}</option>
                    <option value="venues">{t('search.entity.venues')}</option>
                    <option value="sessions">{t('search.entity.sessions')}</option>
                  </select>
                </div>
              </div>

              {(filterForm.type === 'multiselect' || filterForm.type === 'range') && (
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('search.options')}
                  </label>
                  <input
                    type="text"
                    value={filterForm.options}
                    onChange={(e) => setFilterForm({ ...filterForm, options: e.target.value })}
                    className="input-dark w-full"
                    placeholder="option1, option2, option3"
                  />
                  <p className="text-xs text-[#B8B8C3] mt-1">{t('search.optionsHint')}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('search.order')}
                </label>
                <input
                  type="number"
                  value={filterForm.order}
                  onChange={(e) => setFilterForm({ ...filterForm, order: Number(e.target.value) })}
                  className="input-dark w-full"
                  min="0"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="filterEnabled"
                  checked={filterForm.enabled}
                  onChange={(e) => setFilterForm({ ...filterForm, enabled: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="filterEnabled" className="text-sm text-[#B8B8C3]">
                  {t('search.enableFilter')}
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveFilter} className="btn-primary flex-1">
                {t('common.save')}
              </button>
              <button
                onClick={() => {
                  setShowFilterModal(false);
                  setEditingFilter(null);
                  resetFilterForm();
                }}
                className="btn-secondary flex-1"
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Filter Modal - For Search */}
      {showApplyFilterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              🔍 {t('search.applyFilters')}
            </h3>

            <div className="space-y-4">
              {filters.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-[#B8B8C3] mb-4">{t('search.noFiltersAvailable')}</p>
                  <p className="text-sm text-[#B8B8C3]">{t('search.contactAdmin')}</p>
                </div>
              ) : (
                filters.filter(f => f.enabled).map(filter => (
                  <div key={filter.id} className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      {filter.name}
                    </label>
                    
                    {filter.type === 'boolean' && (
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={activeFilters[filter.name] || false}
                          onChange={(e) => applyFilter(filter.name, e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-[#B8B8C3]">{t('common.enabled')}</span>
                      </div>
                    )}

                    {filter.type === 'multiselect' && filter.options && (
                      <select
                        multiple
                        value={activeFilters[filter.name] || []}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions, option => option.value);
                          applyFilter(filter.name, selected);
                        }}
                        className="input-dark w-full"
                        size={Math.min(filter.options.length, 5)}
                      >
                        {filter.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    )}

                    {filter.type === 'range' && (
                      <input
                        type="text"
                        value={activeFilters[filter.name] || ''}
                        onChange={(e) => applyFilter(filter.name, e.target.value)}
                        className="input-dark w-full"
                        placeholder={t('search.enterValue')}
                      />
                    )}

                    {filter.type === 'date' && (
                      <input
                        type="date"
                        value={activeFilters[filter.name] || ''}
                        onChange={(e) => applyFilter(filter.name, e.target.value)}
                        className="input-dark w-full"
                      />
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowApplyFilterModal(false);
                  handleSearch();
                }}
                className="btn-primary flex-1"
              >
                {t('search.applyAndSearch')}
              </button>
              <button
                onClick={() => setShowApplyFilterModal(false)}
                className="btn-secondary flex-1"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchManagement;
